import React from "react";
import { consolidationRequest } from "../api/consolidationApi.js";
import { readOrderDataRevision, subscribeOrderDataChange } from "../utils/orderDataSync.js";

// The month read is heavy: refreshing it on every opening slowed the chosen day.
export const FRESH_FOR_MS = 5 * 60_000;

export default function useConsolidationCalendar(month, enabled, refreshKey, isOpen, requestCalendar = consolidationRequest, memory = null) {
  // Propre à la page montée, sauf si la page fournit sa mémoire de session (oubliée au changement de compte) :
  // le mois réapparaît alors aussitôt au retour sur la page.
  const cache = React.useRef(memory || new Map());
  const active = React.useRef(new Map());
  const months = React.useRef(cache.current.get(month)?.data?.available_months || []);
  const currentRefresh = React.useRef(refreshKey);
  currentRefresh.current = refreshKey;
  const [, render] = React.useReducer((value) => value + 1, 0);
  const [generation, invalidateGeneration] = React.useReducer((value) => value + 1, 0);
  const cancel = React.useCallback(() => {
    for (const request of active.current.values()) request.controller.abort();
    active.current.clear();
  }, []);
  const invalidate = React.useCallback(() => {
    cancel();
    cache.current.clear();
    invalidateGeneration();
  }, [cancel]);
  React.useEffect(() => subscribeOrderDataChange(invalidate), [invalidate]);
  // Seul un rechargement ultérieur vide les mois ; monter la page garde ceux déjà vus.
  const shownRefresh = React.useRef(refreshKey);
  React.useEffect(() => {
    if (shownRefresh.current === refreshKey) return;
    shownRefresh.current = refreshKey;
    invalidate();
  }, [refreshKey, invalidate]);
  React.useEffect(() => cancel, [cancel]);

  const load = React.useCallback(({ force = false } = {}) => {
    const revision = readOrderDataRevision();
    const stored = cache.current.get(month);
    const matches = (entry) => entry?.revision === revision && entry?.refreshKey === refreshKey;
    if (!force && matches(stored) && stored.data && Date.now() - stored.loadedAt < FRESH_FOR_MS) {
      return Promise.resolve(stored.data);
    }
    const pending = active.current.get(month);
    if (!force && matches(pending)) return pending.promise;
    pending?.controller.abort();
    const controller = new AbortController();
    const request = { controller, revision, refreshKey };
    active.current.set(month, request);
    // Expiry alone keeps the month visible while it refreshes; a change or a retry hides it.
    const expired = !force && matches(stored) && stored.data ? stored : null;
    cache.current.set(month, { revision, refreshKey, data: expired?.data ?? null, error: null,
      loadedAt: expired?.loadedAt, refreshing: Boolean(expired) });
    render();
    const isCurrent = () => !controller.signal.aborted && active.current.get(month) === request
      && revision === readOrderDataRevision() && refreshKey === currentRefresh.current;
    request.promise = (async () => {
      try {
        const data = await requestCalendar(`/calendar?${new URLSearchParams({ month })}`, { signal: controller.signal });
        if (!isCurrent()) return null;
        if (data?.month !== month || !Array.isArray(data.days) || !Array.isArray(data.available_months)) {
          throw new Error("Le calendrier de consolidation reçu est incomplet.");
        }
        cache.current.set(month, { revision, refreshKey, data, error: null, loadedAt: Date.now() });
        months.current = data.available_months;
        render();
        return data;
      } catch (error) {
        if (isCurrent()) {
          cache.current.set(month, { revision, refreshKey, data: null, error });
          render();
        }
        return null;
      } finally {
        if (active.current.get(month) === request) active.current.delete(month);
      }
    })();
    return request.promise;
  }, [month, refreshKey, requestCalendar]);
  React.useEffect(() => {
    if (enabled) void load();
    // Closing the popover keeps both the cached month and its in-flight read.
  }, [enabled, isOpen, load, generation]);
  const entry = cache.current.get(month);
  const current = entry?.revision === readOrderDataRevision() && entry?.refreshKey === refreshKey ? entry : null;
  return { data: current?.data, months: months.current, error: current?.error,
    loading: !current?.data && !current?.error, refreshing: Boolean(current?.refreshing), reload: () => load({ force: true }) };
}
